import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Product {
  id: string;
  name: string;
  price: number;
  image_url: string;
  custom_talent_reward: number | null;
  talent_reward_type: 'percentage' | 'fixed';
}

export default function CommercePreview() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, price, image_url, custom_talent_reward, talent_reward_type')
        .order('created_at', { ascending: false })
        .limit(9);

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('상품 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTalentReward = (product: Product) => {
    if (product.custom_talent_reward) {
      if (product.talent_reward_type === 'fixed') {
        return product.custom_talent_reward;
      } else {
        return Math.floor(product.price * (product.custom_talent_reward / 100));
      }
    }
    return Math.floor(product.price * 0.01);
  };

  const itemsPerPage = 6;
  const totalPages = Math.ceil(products.length / itemsPerPage);
  
  const displayedProducts = products.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const handlePrevPage = () => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : totalPages - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : 0));
  };

  if (loading) {
    return (
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-white" data-product-shop>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between mb-12">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              MUST몰
            </h2>
            <p className="text-lg text-gray-600">
              MUST몰만의 혜택이 담긴 다양한 상품을 만나보세요
            </p>
          </div>
          <Link
            to="/commerce"
            className="hidden md:inline-block px-6 py-3 border-2 border-teal-600 text-teal-600 font-semibold rounded-lg hover:bg-teal-600 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
          >
            MUST몰 가기
          </Link>
        </div>

        <div className="relative">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayedProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => navigate(`/commerce/product/${product.id}`)}
                className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 group cursor-pointer"
                data-product-shop
              >
                <div className="relative w-full h-72 overflow-hidden bg-gray-50">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                    {product.name}
                  </h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xl font-bold text-gray-900">
                        {product.price.toLocaleString()}원
                      </p>
                      <div className="flex items-center space-x-1 text-amber-600 mt-1">
                        <i className="ri-coin-line text-sm"></i>
                        <span className="text-sm font-semibold">{calculateTalentReward(product).toLocaleString()}</span>
                      </div>
                    </div>
                    <button className="w-10 h-10 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center cursor-pointer">
                      <i className="ri-shopping-cart-line text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 네비게이션 버튼 */}
          {totalPages > 1 && (
            <>
              <button
                onClick={handlePrevPage}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white border-2 border-gray-300 rounded-full shadow-lg hover:bg-teal-600 hover:border-teal-600 hover:text-white transition-all flex items-center justify-center cursor-pointer z-10"
              >
                <i className="ri-arrow-left-s-line text-2xl"></i>
              </button>
              <button
                onClick={handleNextPage}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white border-2 border-gray-300 rounded-full shadow-lg hover:bg-teal-600 hover:border-teal-600 hover:text-white transition-all flex items-center justify-center cursor-pointer z-10"
              >
                <i className="ri-arrow-right-s-line text-2xl"></i>
              </button>

              {/* 페이지 인디케이터 */}
              <div className="flex items-center justify-center space-x-2 mt-8">
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                      currentPage === index
                        ? 'bg-teal-600 w-8'
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="text-center mt-8 md:hidden">
          <Link
            to="/commerce"
            className="inline-block px-6 py-3 border-2 border-teal-600 text-teal-600 font-semibold rounded-lg hover:bg-teal-600 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
          >
            MUST몰 가기
          </Link>
        </div>
      </div>
    </section>
  );
}
